/**
 * Card displaying commit details including branch, message, and SHA.
 */
import type { FC } from "react";

import {
  type ClipboardStatus,
  useClipboard,
} from "../../../hooks/useClipboard";
import { Button } from "../../common/Button";

function copyShaLabel(status: ClipboardStatus): string {
  switch (status) {
    case "copied": {
      return "Copied \u2713";
    }
    case "failed": {
      return "Failed";
    }
    case "idle": {
      return "Copy full SHA";
    }
  }
}
import {
  CodeIcon,
  CommitIcon,
  ExternalLinkIcon,
  HashIcon,
} from "../../common/icons";
import { SectionHeader } from "../../common/SectionHeader";
import { SummaryFormatter } from "../../common/SummaryFormatter";
import { Text } from "../../common/Text";

interface CommitDetailsCardProps {
  commitSha?: string | null;
  commitUrl?: string | null;
  commitMessage?: string | null;
  branchName?: string | null;
  commitProviderLabel: string;
}

export const CommitDetailsCard: FC<CommitDetailsCardProps> = ({
  commitSha,
  commitUrl,
  commitMessage,
  branchName,
  commitProviderLabel,
}) => {
  const { status: copyStatus, copy } = useClipboard();

  const handleCopySha = () => {
    if (!commitSha) {
      return;
    }
    void copy(commitSha);
  };

  return (
    <div>
      <SectionHeader title="Commit Details" icon={<CommitIcon size={16} />} />

      <div className="overflow-hidden rounded-none border border-slate-300 bg-white shadow-[4px_4px_0_rgba(15,23,42,0.08)] dark:border-slate-700 dark:bg-slate-950/50 dark:shadow-[4px_4px_0_rgba(15,23,42,0.24)]">
        {/* Card Header: Branch & Link */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/70">
          <div className="flex min-w-0 items-center gap-3">
            {branchName ? (
              <div className="flex min-w-0 items-center gap-2">
                <CodeIcon size={16} className="flex-shrink-0 text-slate-500" />
                <span
                  className="truncate font-mono text-[11px] uppercase tracking-[0.18em] text-slate-700 dark:text-slate-200"
                  title={branchName}
                >
                  {branchName}
                </span>
              </div>
            ) : (
              <span className="text-xs italic text-gray-400">
                No branch info
              </span>
            )}
          </div>

          {commitUrl && (
            <a
              href={commitUrl}
              target="_blank"
              rel="noreferrer"
              className="ml-4 flex flex-shrink-0 items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-700 transition-colors hover:text-emerald-800 dark:text-emerald-300 dark:hover:text-emerald-200"
            >
              <span>View on {commitProviderLabel}</span>
              <ExternalLinkIcon size={14} />
            </a>
          )}
        </div>

        {/* Card Body: Message */}
        {commitMessage && (
          <div className="whitespace-pre-wrap bg-white px-4 py-4 font-mono text-sm leading-relaxed text-slate-700 dark:bg-transparent dark:text-slate-300">
            <SummaryFormatter text={commitMessage.trim()} />
          </div>
        )}

        {/* Card Footer: SHA */}
        {commitSha && (
          <div className="border-t border-slate-200 bg-slate-50 px-4 py-2.5 dark:border-slate-800 dark:bg-slate-900/70">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                <HashIcon size={14} className="text-slate-400" />
                <span title={commitSha} aria-label={`Commit ${commitSha}`}>
                  {commitSha.slice(0, 12)}
                  {commitSha.length > 12 ? "…" : ""}
                </span>
                {commitSha.length > 12 && <Text>(full SHA below)</Text>}
              </div>
              <Button
                type="button"
                onClick={handleCopySha}
                variant="outline"
                size="sm"
                className="self-start md:self-auto"
              >
                {copyShaLabel(copyStatus)}
              </Button>
            </div>
            {commitSha.length > 12 && (
              <Text as="p" className="mt-2 break-words">
                Full SHA:{" "}
                <span className="font-mono text-xs text-slate-900 dark:text-slate-100">
                  {commitSha}
                </span>
              </Text>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
